import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, X, Send, ChevronLeft, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, API_BASE, apiGet, apiPost } from '../contexts/AuthContext';

// Build WebSocket URL. Prefer same-origin (nginx proxies /api/ including ws upgrade).
function buildWsUrl(token) {
  const base = API_BASE || window.location.origin;
  const wsBase = base.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws?token=${encodeURIComponent(token)}`;
}

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default function ChatWidget() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isPartner = user?.role === 'partner';

  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedPeer, setSelectedPeer] = useState(null); // peer_id for admin; "admin" for partner
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [connected, setConnected] = useState(false);
  const [filter, setFilter] = useState('');
  const [sending, setSending] = useState(false);

  const wsRef = useRef(null);
  const reconnectRef = useRef({ attempts: 0, timer: null });
  const openRef = useRef(open);
  const selectedRef = useRef(selectedPeer);
  const messagesEndRef = useRef(null);
  // Hold the latest callback identities so the WS effect doesn't tear down
  // and reconnect every time a callback identity changes (which happens on
  // every state update). Without this, the socket would loop forever in
  // "Reconnecting…" state.
  const refreshConversationsRef = useRef(() => {});
  const refreshUnreadRef = useRef(() => {});
  const markReadRef = useRef(() => {});

  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { selectedRef.current = selectedPeer; }, [selectedPeer]);

  const refreshConversations = useCallback(async () => {
    if (!user) return;
    try {
      const res = await apiGet('/chat/conversations');
      const data = res?.data;
      setConversations(Array.isArray(data) ? data : []);
    } catch (e) {
      // silent
    }
  }, [user]);

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const res = await apiGet('/chat/unread');
      setUnreadTotal(res?.data?.unread || 0);
    } catch {
      /* ignore */
    }
  }, [user]);

  const loadMessages = useCallback(async (peerId) => {
    if (!peerId) return;
    try {
      const res = await apiGet(`/chat/messages/${encodeURIComponent(peerId)}`);
      const data = res?.data;
      setMessages(Array.isArray(data) ? data : []);
    } catch {
      setMessages([]);
    }
  }, []);

  // One chat message sets off a burst of events (chat, our own mark-read, the
  // server's 'read' echo). Each used to re-fetch the conversation list and
  // unread count; collapse a burst into a single re-fetch.
  const refreshTimerRef = useRef(null);
  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) return;
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      refreshConversationsRef.current();
      refreshUnreadRef.current();
    }, 400);
  }, []);

  const markRead = useCallback(async (peerId) => {
    if (!peerId) return;
    try {
      await apiPost(`/chat/read/${encodeURIComponent(peerId)}`, {});
      scheduleRefresh();
      window.dispatchEvent(new CustomEvent('xp:refresh-notifications'));
    } catch {
      /* ignore */
    }
  }, [scheduleRefresh]);

  // Keep refs pointing at the latest callbacks so the WS effect can call them
  // without re-subscribing on every render.
  useEffect(() => { refreshConversationsRef.current = refreshConversations; }, [refreshConversations]);
  useEffect(() => { refreshUnreadRef.current = refreshUnread; }, [refreshUnread]);
  useEffect(() => { markReadRef.current = markRead; }, [markRead]);

  // WebSocket lifecycle
  useEffect(() => {
    if (!user) return undefined;
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    let closedByUs = false;
    let pingTimer = null;

    const connect = () => {
      try {
        const ws = new WebSocket(buildWsUrl(token));
        wsRef.current = ws;

        ws.onopen = () => {
          setConnected(true);
          reconnectRef.current.attempts = 0;
          // Keep the connection alive through Synology's idle-timeout (~60 s)
          // by sending a ping every 25 seconds.
          pingTimer = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              try { ws.send(JSON.stringify({ type: 'ping' })); } catch { /* ignore */ }
            }
          }, 25000);
        };

        ws.onmessage = (evt) => {
          let data;
          try { data = JSON.parse(evt.data); } catch { return; }
          if (data?.type === 'pong') return; // heartbeat reply — nothing to do
          if (data?.type === 'chat' && data.message) {
            const m = data.message;
            const myId = user._id || user.id;

            // Determine the peer_id of this message relative to current user
            let peerOfMsg;
            if (isAdmin) {
              peerOfMsg = m.partner_id; // admin's conversation peer is the partner
            } else {
              peerOfMsg = 'admin';
            }

            const isMine = m.from_user_id === myId;
            const currentlyViewing = openRef.current && selectedRef.current === peerOfMsg;

            // Append to open conversation
            if (currentlyViewing) {
              setMessages((prev) => {
                if (prev.some((x) => x.id === m.id)) return prev;
                return [...prev, m];
              });
              // auto-mark as read if it's from the peer
              if (!isMine) markReadRef.current(peerOfMsg);
            }

            // Update conversations list + unread counts
            scheduleRefresh();

            // Toast only for incoming messages (not our own echoes) when not actively viewing that thread
            if (!isMine && !currentlyViewing) {
              const preview = (m.text || '').slice(0, 80);
              toast(`${m.from_name || 'New message'}`, {
                duration: 5000,
                description: preview,
                onClick: () => {
                  setSelectedPeer(peerOfMsg);
                  setOpen(true);
                },
                action: {
                  label: 'Open',
                  onClick: () => {
                    setSelectedPeer(peerOfMsg);
                    setOpen(true);
                  },
                },
              });
            }
          } else if (data?.type === 'read') {
            scheduleRefresh();
          }
        };

        ws.onclose = (event) => {
          clearInterval(pingTimer);
          pingTimer = null;
          setConnected(false);
          wsRef.current = null;
          if (closedByUs) return;
          // 4001 = invalid/expired token, 4003 = account inactive.
          // These are permanent auth errors — retrying won't help. Stop here;
          // the auth context will handle logging the user out if needed.
          if (event.code === 4001 || event.code === 4003) return;
          const attempt = Math.min(reconnectRef.current.attempts + 1, 6);
          reconnectRef.current.attempts = attempt;
          const delay = Math.min(1000 * 2 ** (attempt - 1), 30000);
          reconnectRef.current.timer = setTimeout(connect, delay);
        };

        ws.onerror = () => {
          try { ws.close(); } catch { /* ignore */ }
        };
      } catch {
        // swallow and try again
        reconnectRef.current.timer = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      closedByUs = true;
      clearInterval(pingTimer);
      pingTimer = null;
      if (reconnectRef.current.timer) clearTimeout(reconnectRef.current.timer);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id || user?._id, isAdmin]);

  // Initial data
  useEffect(() => {
    if (!user) return;
    refreshUnread();
    refreshConversations();
    if (isPartner) setSelectedPeer('admin');
  }, [user, isPartner, refreshUnread, refreshConversations]);

  // Let the notification bell open this widget on the right conversation
  // when the user clicks a "New chat message" notification.
  useEffect(() => {
    const handler = (e) => {
      const peerId = e?.detail?.peerId;
      if (peerId) setSelectedPeer(peerId);
      setOpen(true);
    };
    window.addEventListener('xp:open-chat', handler);
    return () => window.removeEventListener('xp:open-chat', handler);
  }, []);

  // When a peer is selected or widget opens, load messages and mark read
  useEffect(() => {
    if (open && selectedPeer) {
      loadMessages(selectedPeer);
      markRead(selectedPeer);
    }
  }, [open, selectedPeer, loadMessages, markRead]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const toUserId = isPartner ? 'admin' : selectedPeer;
    if (!toUserId) return;
    setSending(true);
    try {
      await apiPost('/chat/send', { to_user_id: toUserId, text });
      setInput('');
      // Echo will arrive over ws; but if ws is down, reload manually
      if (!connected) {
        loadMessages(selectedPeer);
        refreshConversations();
      }
    } catch (e) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const filteredConversations = useMemo(() => {
    if (!filter.trim()) return conversations;
    const q = filter.trim().toLowerCase();
    return conversations.filter(
      (c) =>
        (c.peer_name || '').toLowerCase().includes(q) ||
        (c.peer_email || '').toLowerCase().includes(q) ||
        (c.peer_organization || '').toLowerCase().includes(q)
    );
  }, [conversations, filter]);

  if (!user || !(isAdmin || isPartner)) return null;

  // Bubble in the bottom-right corner; the panel opens above it. (WhatsApp support is in the header's Contact menu.)
  const bubblePos = 'bottom-6 right-6';
  const panelPos = 'bottom-24 right-6';

  // Responsive panel size — never wider/taller than viewport, with comfortable
  // gaps so it never overlaps the floating bubbles below it.
  const panelWidth = isAdmin ? 480 : 360;
  const panelHeight = 540;
  const panelStyle = {
    width: `min(${panelWidth}px, calc(100vw - 48px))`,
    height: `min(${panelHeight}px, calc(100dvh - ${isAdmin ? 120 : 200}px))`,
  };

  const currentConvo = conversations.find((c) => c.peer_id === selectedPeer);
  const headerTitle = isPartner
    ? 'Admin Team'
    : currentConvo?.peer_name || 'Select a partner';

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`fixed ${bubblePos} z-50 flex items-center justify-center w-14 h-14 bg-brand hover:bg-brand/90 text-white rounded-full shadow-lift hover:shadow-pop transition-all`}
          title="Live Chat"
        >
          <MessageCircle className="w-6 h-6" />
          {unreadTotal > 0 && (
            <span className="absolute top-0 right-0 bg-ember text-white dark:text-canvas text-xs rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center font-semibold translate-x-1 -translate-y-1 shadow-sm">
              {unreadTotal > 99 ? '99+' : unreadTotal}
            </span>
          )}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className={`fixed ${panelPos} z-50 bg-surface rounded-xl shadow-pop border border-contrast/[0.08] flex flex-col overflow-hidden`}
          style={panelStyle}
          data-testid="live-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 text-white" style={{ background: 'linear-gradient(135deg, #3B82F6, #2563EB)' }}>
            <div className="flex items-center gap-2 min-w-0">
              {isAdmin && selectedPeer && (
                <button onClick={() => setSelectedPeer(null)} aria-label="Back to conversations" className="md:hidden p-1 hover:bg-white/10 rounded">
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              <MessageCircle className="w-4 h-4 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{headerTitle}</div>
                <div className="text-[10px] text-brand">
                  {connected ? 'Connected' : 'Reconnecting…'}
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex flex-1 min-h-0">
            {/* Admin: partner list */}
            {isAdmin && (
              <div className="w-56 border-r border-contrast/[0.08] flex flex-col bg-surface-2">
                <div className="p-2 border-b border-contrast/[0.08] bg-surface">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-3" />
                    <input
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      placeholder="Search partners…"
                      aria-label="Search partners"
                      className="w-full pl-7 pr-2 py-1.5 text-xs bg-surface text-ink placeholder:text-ink-3 border border-contrast/10 rounded focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {filteredConversations.length === 0 && (
                    <div className="p-3 text-xs text-ink-3">No partners</div>
                  )}
                  {filteredConversations.map((c) => (
                    <button
                      key={c.peer_id}
                      onClick={() => setSelectedPeer(c.peer_id)}
                      className={`w-full text-left px-3 py-2 border-b border-contrast/[0.06] hover:bg-surface-3 transition-colors ${
                        selectedPeer === c.peer_id ? 'bg-surface-3 border-l-2 border-l-brand' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-xs font-semibold text-ink truncate">{c.peer_name || 'Partner'}</span>
                        {c.unread_count > 0 && (
                          <span className="bg-ember text-white dark:text-canvas text-[10px] rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center font-semibold">
                            {c.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-ink-3 truncate">
                        {c.last_message?.text || c.peer_organization || c.peer_email || '—'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Message pane */}
            <div className="flex-1 flex flex-col min-w-0">
              {isAdmin && !selectedPeer ? (
                <div className="flex-1 flex items-center justify-center text-sm text-ink-3 p-4 text-center">
                  Select a partner to start chatting
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-surface">
                    {messages.length === 0 && (
                      <div className="text-center text-xs text-ink-3 py-8">
                        No messages yet. Say hello!
                      </div>
                    )}
                    {messages.map((m) => {
                      const mine = m.from_user_id === (user._id || user.id);
                      return (
                        <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[78%] px-3 py-2 rounded-lg text-sm shadow-sm ${
                              mine
                                ? 'bg-brand text-white rounded-br-none'
                                : 'bg-surface-2 text-ink border border-contrast/[0.06] rounded-bl-none'
                            }`}
                          >
                            {!mine && (
                              <div className="text-[10px] font-semibold opacity-75 mb-0.5">
                                {m.from_name || 'Admin'}
                              </div>
                            )}
                            <div className="whitespace-pre-wrap break-words">{m.text}</div>
                            <div className={`text-[10px] mt-1 ${mine ? 'text-white/70' : 'text-ink-3'}`}>
                              {formatTime(m.created_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Composer */}
                  <div className="border-t border-contrast/[0.08] p-2 flex items-end gap-2 bg-surface">
                    <textarea
                      rows={1}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type a message…"
                      aria-label="Message"
                      title="Enter to send, Shift+Enter for a new line"
                      className="flex-1 resize-none bg-surface text-ink placeholder:text-ink-3 border border-contrast/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 max-h-32"
                    />
                    <button
                      type="button"
                      onClick={handleSend}
                      disabled={!input.trim() || sending}
                      className="bg-brand hover:bg-brand/90 disabled:bg-contrast/20 text-white p-2 rounded-lg transition-colors"
                      aria-label="Send"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
